import { Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';

// ============================================================
// Bundle splitting (Phase 26)
//
// Strategie : eagerly load uniquement le critical path utilisateur
// (Login, Dashboard, Upload, TenderDetail). Tout le reste — admin,
// help, audit, settings — est lazy-loaded pour reduire le main
// bundle de ~625 KB → < 400 KB gzip.
//
// Les pages exportees en named export sont wrappees via .then().
// ============================================================

import { Dashboard } from './pages/Dashboard';
import { Upload } from './pages/Upload';
import { TenderDetail } from './pages/TenderDetail';
import { Login } from './pages/Login';
import { AuthVerify } from './pages/AuthVerify';

// User : peu utilises en hot path
const TenderPrint = lazy(() => import('./pages/TenderPrint').then((m) => ({ default: m.TenderPrint })));
const PublicTender = lazy(() => import('./pages/PublicTender').then((m) => ({ default: m.PublicTender })));
const Audit = lazy(() => import('./pages/Audit').then((m) => ({ default: m.Audit })));
const Catalogue = lazy(() => import('./pages/Catalogue').then((m) => ({ default: m.Catalogue })));
const StatusPublic = lazy(() => import('./pages/StatusPublic').then((m) => ({ default: m.StatusPublic })));

// Admin — toutes lazy
const LearningCorpus = lazy(() => import('./pages/admin/LearningCorpus').then((m) => ({ default: m.LearningCorpus })));
const RuleProposals = lazy(() => import('./pages/admin/RuleProposals').then((m) => ({ default: m.RuleProposals })));
const AdminUsers = lazy(() => import('./pages/admin/Users').then((m) => ({ default: m.AdminUsers })));
const Logs = lazy(() => import('./pages/admin/Logs').then((m) => ({ default: m.Logs })));
const PlatformSecrets = lazy(() => import('./pages/admin/PlatformSecrets').then((m) => ({ default: m.PlatformSecrets })));
const AdminMonitoring = lazy(() => import('./pages/admin/Monitoring').then((m) => ({ default: m.AdminMonitoring })));
const CatalogueVersionDetail = lazy(() => import('./pages/admin/CatalogueVersionDetail').then((m) => ({ default: m.CatalogueVersionDetail })));
const Diagnostic = lazy(() => import('./pages/admin/Diagnostic').then((m) => ({ default: m.Diagnostic })));
const AdminHome = lazy(() => import('./pages/admin/AdminHome').then((m) => ({ default: m.AdminHome })));
const AdminLayout = lazy(() => import('./components/AdminLayout').then((m) => ({ default: m.AdminLayout })));


// Components legers, gardes eager
import { NotificationsBell } from './components/NotificationsBell';
import { ThemeToggle } from './components/ThemeToggle';
import { Footer } from './components/Footer';
import { IconButton } from './components/IconButton';
import { LogOut, Menu } from 'lucide-react';
import { useT } from './lib/i18n';
import { api, apiHref, setSessionToken } from './lib/api-client';

const BRAND_NAME = 'GS Copilot';

export function App() {
  const { t } = useT();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [canAdmin, setCanAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/public/') || location.pathname === '/login' || location.pathname === '/auth/verify') {
      return;
    }
    let cancelled = false;
    api
      .get<{ permissions?: string[]; isSuperadmin?: boolean; platformRole?: string }>('/api/auth/me')
      .then((me) => {
        if (cancelled) return;
        const permissions = me.permissions ?? [];
        setCanAdmin(Boolean(me.isSuperadmin) || me.platformRole === 'platform_admin' || permissions.includes('tender:admin'));
      })
      .catch(() => {
        if (!cancelled) setCanAdmin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Route publique (share link, status page) : layout bypass — pas de header
  if (location.pathname.startsWith('/public/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/public/tenders/:token" element={<PublicTender />} />
          <Route path="/public/status" element={<StatusPublic />} />
        </Routes>
      </Suspense>
    );
  }

  // Login / verify magic-link : layout minimal sans header
  if (location.pathname === '/login' || location.pathname === '/auth/verify') {
    return (
      <div className="min-h-screen bg-ink-950 text-steel-100">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/verify" element={<AuthVerify />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-950 text-steel-100 antialiased flex flex-col">
      {/* <AgentFloating /> retire : la conversation appartient a chaque AO */}

      {/* HEADER : barre fine industrielle, full-width, monochrome */}
      <header className="border-b border-ink-700 bg-ink-950 sticky top-0 z-30 backdrop-blur-sm">
        <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between gap-6">
          {/* Brand */}
          <div className="flex items-center gap-6 min-w-0">
            <IconButton
              icon={Menu}
              label="Menu"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              className="md:hidden"
            />
            <Link to="/" className="flex items-center gap-2.5 min-w-0">
              <div className="w-2 h-2 bg-brand-500" aria-hidden />
              <span className="font-display text-base tracking-tight whitespace-nowrap">
                {BRAND_NAME}
              </span>
            </Link>
          </div>

          {/* Nav principale */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center" aria-label="Main">
            <HeaderLink to="/" tourId="dashboard-link">{t('nav.dashboard')}</HeaderLink>
            {canAdmin && <HeaderLink to="/admin">Admin</HeaderLink>}
          </nav>

          {/* Right tools */}
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <NotificationsBell />
            <IconButton
              icon={LogOut}
              label="Se déconnecter"
              size="sm"
              tooltipSide="bottom"
              onClick={async () => {
                setSessionToken(null); // purge le JWT Bearer stocké
                window.location.href = apiHref('/api/auth/logout');
              }}
            />
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-ink-700 bg-ink-900">
            <nav
              className="max-w-[1600px] mx-auto flex flex-col px-2 py-2"
              aria-label="Main mobile"
              onClick={() => setMobileMenuOpen(false)}
            >
              <HeaderLink to="/">{t('nav.dashboard')}</HeaderLink>
              {canAdmin && <HeaderLink to="/admin">Admin</HeaderLink>}
            </nav>
          </div>
        )}
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8 flex-1 w-full">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* User routes */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/tenders/:id" element={<TenderDetail />} />
          <Route path="/tenders/:id/print" element={<TenderPrint />} />
          <Route path="/tenders/:id/audit" element={<Audit />} />
          {/* Admin : layout avec sidebar persistante */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminHome />} />
            <Route path="catalogue" element={<Catalogue />} />
            <Route path="catalogue/:id" element={<CatalogueVersionDetail />} />
            <Route path="corpus" element={<LearningCorpus />} />
            <Route path="rule-proposals" element={<RuleProposals />} />
            <Route path="api-keys" element={<PlatformSecrets />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="monitoring" element={<AdminMonitoring />} />
            <Route path="diagnostic" element={<Diagnostic />} />
            <Route path="logs" element={<Logs />} />
          </Route>
        </Routes>
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}

/**
 * Fallback minimaliste pendant le lazy-load d'une route.
 * Reste tres discret pour ne pas flasher (chunks typiques < 50 KB → < 100 ms).
 */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="text-2xs font-mono uppercase tracking-widest text-steel-500 animate-pulse">
        loading…
      </div>
    </div>
  );
}

function HeaderLink({
  to,
  children,
  tourId,
}: {
  to: string;
  children: React.ReactNode;
  tourId?: string;
}) {
  const location = useLocation();

  // Match logic personnalise pour eviter le double-actif :
  //  - "/" : exact uniquement
  //  - "/admin" : prefix sur toute l'administration
  const path = location.pathname;
  let isActive: boolean;
  if (to === '/') {
    isActive = path === '/';
  } else if (to === '/admin') {
    isActive = path === '/admin' || path.startsWith('/admin/');
  } else {
    isActive = path === to || path.startsWith(`${to}/`);
  }

  return (
    <NavLink
      to={to}
      data-onboarding-target={tourId}
      className={`px-3 py-1.5 text-sm tracking-tight transition-colors whitespace-nowrap ${
        isActive ? 'text-amber-500' : 'text-steel-300 hover:text-steel-100'
      }`}
    >
      {children}
    </NavLink>
  );
}
